import React, { useState, useEffect, useCallback } from "react";
import {
    getInvoiceSettings, updateInvoiceSettings, InvoiceSettings,
    listInvoiceProjectSettings, updateInvoiceProjectSettings, InvoiceProjectSetting,
    listInvoiceCustomers, upsertInvoiceCustomer, InvoiceCustomer,
} from "../services/api";

const primaryBtnStyle: React.CSSProperties = {
    backgroundColor: "#F18E00", color: "white", border: "none", borderRadius: "4px",
    padding: "8px 18px", cursor: "pointer", fontSize: "13px", fontWeight: "bold",
};
const smallBtnStyle: React.CSSProperties = {
    backgroundColor: "#5C5D5E", color: "white", border: "none", borderRadius: "4px",
    padding: "5px 12px", cursor: "pointer", fontSize: "11px",
};
const sectionStyle: React.CSSProperties = {
    backgroundColor: "white", border: "1px solid #e0e0e0", borderRadius: "6px",
    padding: "16px 20px", marginBottom: "20px",
};
const inputStyle: React.CSSProperties = {
    padding: "6px 10px", fontSize: "13px", border: "1px solid #ddd", borderRadius: "4px",
    boxSizing: "border-box", width: "100%",
};
const labelStyle: React.CSSProperties = {
    display: "block", fontSize: "12px", color: "#5C5D5E", fontWeight: "bold", marginBottom: "4px",
};
const thStyle: React.CSSProperties = {
    textAlign: "left", padding: "8px 10px", fontSize: "12px",
    color: "white", backgroundColor: "#5C5D5E", whiteSpace: "nowrap",
};
const tdStyle: React.CSSProperties = {
    textAlign: "left", padding: "6px 10px", fontSize: "13px", borderBottom: "1px solid #f0f0f0",
};

// -----------------------------------------------------------------------
// T&M Invoice Automation settings (2026-08-28 build, BACKLOG.md step 10) --
// email template + the one-time Zoho Revenue Type custom field lookup +
// SharePoint output folder. Code only for now -- staying unbuilt/
// undeployed until Pete reviews the whole feature.
// -----------------------------------------------------------------------

const InvoiceSettingsSection: React.FC = () => {
    const [settings, setSettings] = useState<InvoiceSettings | null>(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [saveMsg, setSaveMsg] = useState<string | null>(null);

    useEffect(() => {
        getInvoiceSettings().then(setSettings).catch(e => console.error("Failed to load invoice settings", e)).finally(() => setLoading(false));
    }, []);

    const handleSave = async () => {
        if (!settings) return;
        setSaving(true);
        setSaveMsg(null);
        try {
            const updated = await updateInvoiceSettings(settings);
            setSettings(updated);
            setSaveMsg("Saved.");
        } catch (e) {
            console.error("Failed to save invoice settings", e);
            setSaveMsg("Failed to save. Please try again.");
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <div style={{ fontSize: "13px", color: "#999" }}>Loading…</div>;
    if (!settings) return null;

    return (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
            <div>
                <label style={labelStyle}>Zoho "Revenue Type" custom field ID</label>
                <input style={inputStyle} value={settings.revenue_type_field_id}
                    onChange={e => setSettings({ ...settings, revenue_type_field_id: e.target.value })}
                    placeholder="e.g. 3944445000000123456" />
            </div>
            <div>
                <label style={labelStyle}>Revenue Type value to set</label>
                <input style={inputStyle} value={settings.revenue_type_value}
                    onChange={e => setSettings({ ...settings, revenue_type_value: e.target.value })} />
            </div>
            <div style={{ gridColumn: "1 / -1" }}>
                <label style={labelStyle}>SharePoint folder for generated invoice PDFs</label>
                <input style={inputStyle} value={settings.invoice_output_path}
                    onChange={e => setSettings({ ...settings, invoice_output_path: e.target.value })}
                    placeholder="e.g. Invoices/Generated" />
            </div>
            <div style={{ gridColumn: "1 / -1" }}>
                <label style={labelStyle}>Email subject template</label>
                <input style={inputStyle} value={settings.email_subject_template}
                    onChange={e => setSettings({ ...settings, email_subject_template: e.target.value })} />
            </div>
            <div style={{ gridColumn: "1 / -1" }}>
                <label style={labelStyle}>Email body template (HTML)</label>
                <textarea style={{ ...inputStyle, fontFamily: "monospace", minHeight: "140px" }} rows={6}
                    value={settings.email_body_template}
                    onChange={e => setSettings({ ...settings, email_body_template: e.target.value })} />
                <div style={{ fontSize: "11px", color: "#999", marginTop: "4px" }}>
                    Tokens: {"{{invoice_number}}"} {"{{po_number}}"} {"{{project_name}}"} {"{{period_start}}"}{" "}
                    {"{{period_end}}"} {"{{amount_due}}"} {"{{due_date}}"}
                </div>
            </div>
            <div style={{ gridColumn: "1 / -1", display: "flex", alignItems: "center", gap: "10px" }}>
                <button style={{ ...primaryBtnStyle, opacity: saving ? 0.6 : 1 }} disabled={saving} onClick={handleSave}>
                    {saving ? "Saving…" : "Save"}
                </button>
                {saveMsg && <span style={{ fontSize: "12px", color: saveMsg.startsWith("Failed") ? "#C00000" : "#2E7D32" }}>{saveMsg}</span>}
            </div>
        </div>
    );
};

const GROUPING_OPTIONS = [
    { value: "", label: "(default for billing type)" },
    { value: "users", label: "Group by Users" },
    { value: "tasks_and_users", label: "Group by Tasks and Users" },
];

const ProjectBillingSettingsSection: React.FC = () => {
    const [projects, setProjects] = useState<InvoiceProjectSetting[]>([]);
    const [loading, setLoading] = useState(true);
    const [drafts, setDrafts] = useState<Record<number, Partial<InvoiceProjectSetting>>>({});
    const [savingId, setSavingId] = useState<number | null>(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const data = await listInvoiceProjectSettings();
            setProjects(data.projects);
        } catch (e) {
            console.error("Failed to load project billing settings", e);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const draftFor = (p: InvoiceProjectSetting) => ({ ...p, ...drafts[p.project_id] });

    const setDraft = (projectId: number, field: keyof InvoiceProjectSetting, value: string) => {
        setDrafts(prev => ({ ...prev, [projectId]: { ...prev[projectId], [field]: value } }));
    };

    const handleSave = async (projectId: number) => {
        const draft = drafts[projectId];
        if (!draft) return;
        setSavingId(projectId);
        try {
            await updateInvoiceProjectSettings(projectId, {
                billing_grouping: draft.billing_grouping ?? undefined,
                invoice_email_to: draft.invoice_email_to ?? undefined,
                invoice_email_cc: draft.invoice_email_cc ?? undefined,
            });
            setDrafts(prev => { const next = { ...prev }; delete next[projectId]; return next; });
            await load();
        } catch (e) {
            console.error("Failed to save project billing settings", e);
        } finally {
            setSavingId(null);
        }
    };

    if (loading) return <div style={{ fontSize: "13px", color: "#999" }}>Loading…</div>;

    return (
        <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                    <tr>
                        <th style={thStyle}>Project</th>
                        <th style={thStyle}>Billing Grouping</th>
                        <th style={thStyle}>Invoice Email "To"</th>
                        <th style={thStyle}>Invoice Email "CC"</th>
                        <th style={thStyle}></th>
                    </tr>
                </thead>
                <tbody>
                    {projects.map(p => {
                        const d = draftFor(p);
                        const dirty = !!drafts[p.project_id];
                        return (
                            <tr key={p.project_id}>
                                <td style={tdStyle}>{p.project_name}</td>
                                <td style={tdStyle}>
                                    <select style={inputStyle} value={d.billing_grouping || ""}
                                        onChange={e => setDraft(p.project_id, "billing_grouping", e.target.value)}>
                                        {GROUPING_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                                    </select>
                                </td>
                                <td style={tdStyle}>
                                    <input style={inputStyle} value={d.invoice_email_to || ""}
                                        placeholder="blank = no email sent"
                                        onChange={e => setDraft(p.project_id, "invoice_email_to", e.target.value)} />
                                </td>
                                <td style={tdStyle}>
                                    <input style={inputStyle} value={d.invoice_email_cc || ""}
                                        onChange={e => setDraft(p.project_id, "invoice_email_cc", e.target.value)} />
                                </td>
                                <td style={tdStyle}>
                                    {dirty && (
                                        <button style={smallBtnStyle} disabled={savingId === p.project_id}
                                            onClick={() => handleSave(p.project_id)}>
                                            {savingId === p.project_id ? "Saving…" : "Save"}
                                        </button>
                                    )}
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
};

const CustomerAbbreviationsSection: React.FC = () => {
    const [customers, setCustomers] = useState<InvoiceCustomer[]>([]);
    const [loading, setLoading] = useState(true);
    const [newName, setNewName] = useState("");
    const [newAbbrev, setNewAbbrev] = useState("");
    const [saving, setSaving] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const data = await listInvoiceCustomers();
            setCustomers(data.customers);
        } catch (e) {
            console.error("Failed to load customers", e);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const handleAdd = async () => {
        if (!newName.trim() || !newAbbrev.trim()) return;
        setSaving(true);
        try {
            await upsertInvoiceCustomer(newName.trim(), newAbbrev.trim());
            setNewName("");
            setNewAbbrev("");
            await load();
        } catch (e) {
            console.error("Failed to save customer", e);
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <div style={{ fontSize: "13px", color: "#999" }}>Loading…</div>;

    return (
        <div>
            <div style={{ display: "flex", gap: "8px", marginBottom: "14px", alignItems: "flex-end" }}>
                <div>
                    <label style={labelStyle}>Customer Name (matches Zoho exactly)</label>
                    <input style={inputStyle} value={newName} onChange={e => setNewName(e.target.value)} />
                </div>
                <div>
                    <label style={labelStyle}>Abbreviation</label>
                    <input style={inputStyle} value={newAbbrev} onChange={e => setNewAbbrev(e.target.value)} />
                </div>
                <button style={{ ...primaryBtnStyle, opacity: saving ? 0.6 : 1 }} disabled={saving} onClick={handleAdd}>
                    {saving ? "Saving…" : "Add / Update"}
                </button>
            </div>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                    <tr>
                        <th style={thStyle}>Customer Name</th>
                        <th style={thStyle}>Abbreviation</th>
                    </tr>
                </thead>
                <tbody>
                    {customers.map(c => (
                        <tr key={c.id}>
                            <td style={tdStyle}>{c.customer_name}</td>
                            <td style={tdStyle}>{c.abbreviation}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

const SystemConfig: React.FC = () => {
    return (
        <div>
            <h1 style={{ color: "#5C5D5E", margin: "0 0 6px 0", fontSize: "22px" }}>System Config</h1>
            <p style={{ color: "#666", marginTop: 0, marginBottom: "24px", fontSize: "13px" }}>
                System-level maintenance actions. Zoho sync controls have moved to their own "Zoho Sync" page under Admin.
            </p>

            <div style={sectionStyle}>
                <h2 style={{ color: "#5C5D5E", fontSize: "15px", margin: "0 0 12px 0" }}>T&amp;M Invoice Automation Settings</h2>
                <InvoiceSettingsSection />
            </div>

            <div style={sectionStyle}>
                <h2 style={{ color: "#5C5D5E", fontSize: "15px", margin: "0 0 12px 0" }}>
                    T&amp;M Project Billing Settings
                </h2>
                <p style={{ fontSize: "12px", color: "#666", marginTop: 0 }}>
                    Billing Grouping controls how invoice line items are rolled up. If "Invoice Email To" is blank, no
                    email is ever generated or sent for that project -- the PDF is staged for manual handling instead.
                </p>
                <ProjectBillingSettingsSection />
            </div>

            <div style={sectionStyle}>
                <h2 style={{ color: "#5C5D5E", fontSize: "15px", margin: "0 0 12px 0" }}>Customer Abbreviations</h2>
                <p style={{ fontSize: "12px", color: "#666", marginTop: 0 }}>
                    Used to build the renamed invoice PDF filename (Invoice ##### - Abbreviation.YYYY.MM.pdf). A customer
                    with no entry here falls back to the first 3 letters of its name.
                </p>
                <CustomerAbbreviationsSection />
            </div>
        </div>
    );
};

export default SystemConfig;
